import { apiAiChatCompletions } from '../../../../api/ai';
import { finalizeAssistantResponseText } from '../../aiResponseHelpers';
import { createStageResult, createConsolidatePayload } from '../contracts';

// Creates a consolidate-stage result with normalized payload fields.
export const executeConsolidateStage = ({
  triggered = false,
  decisionCode = 'consolidation_gate_not_met',
  payload = {}
} = {}) => {
  return createStageResult({
    stage: 'consolidate',
    status: triggered ? 'applied' : 'skipped',
    decisionCode,
    payload: createConsolidatePayload({
      ...payload,
      triggered
    })
  });
};

// Runs the consolidation sub-orchestration and emits the consolidate stage event.
export const executeConsolidationStage = async ({
  contextRetrieval = null,
  userText = '',
  signal,
  emitStage,
  onModelAttempt,
  consolidationConfig = {},
  executeConsolidateStageFn = executeConsolidateStage,
  requestCompletion = apiAiChatCompletions,
  normalizeContextText,
  extractResponseText,
  summarizeSourceBreakdown,
  summarizeDocSelectionBreakdown
} = {}) => {
  const consolidationEnabled = Boolean(consolidationConfig?.enabled);
  const consolidationMinItems = Math.max(1, Number(consolidationConfig?.minItems || 12));
  const consolidationMinChars = Math.max(200, Number(consolidationConfig?.minChars || 9000));
  const consolidationMaxInputItems = Math.max(1, Number(consolidationConfig?.maxInputItems || 20));
  const consolidationMaxInputChars = Math.max(500, Number(consolidationConfig?.maxInputChars || 18000));
  const consolidationOutputMaxChars = Math.max(250, Number(consolidationConfig?.outputMaxChars || 2600));
  const consolidationKeepTopItems = Math.max(0, Number(consolidationConfig?.keepTopItems || 2));
  const consolidationSummaryMaxPerTurn = Math.max(0, Number(consolidationConfig?.summaryHarvestMaxPerTurn || 0));
  const consolidationSummaryMinScore = Math.max(0, Number(consolidationConfig?.summaryHarvestMinScore || 0));
  const consolidationModel = String(consolidationConfig?.model || '').trim() || null;

  const beforeContextItems = Array.isArray(contextRetrieval?.items) ? contextRetrieval.items.length : 0;
  const beforeContextChars = Number(contextRetrieval?.meta?.selectedChars || 0);

  const emitConsolidateStage = ({
    status = 'skipped',
    decisionCode = 'consolidation_gate_not_met',
    triggered = false,
    afterContextItems = beforeContextItems,
    afterContextChars = beforeContextChars,
    consolidationOutputChars = 0,
    keptOriginalItems = 0,
    reasonCodes = [],
    error = null
  } = {}) => {
    const consolidateResult = executeConsolidateStageFn({
      triggered,
      decisionCode,
      payload: {
        beforeContextItems,
        beforeContextChars,
        afterContextItems,
        afterContextChars,
        gateMinItems: consolidationMinItems,
        gateMinChars: consolidationMinChars,
        maxInputItems: consolidationMaxInputItems,
        maxInputChars: consolidationMaxInputChars,
        consolidationModel: consolidationModel || null,
        consolidationOutputChars,
        keptOriginalItems
      }
    });

    if (typeof emitStage === 'function') {
      emitStage('consolidate', {
        status,
        decisionCode: consolidateResult?.decisionCode || decisionCode,
        reasonCodes,
        ...consolidateResult.payload,
        ...(error ? { error } : {})
      });
    }
  };

  const shouldConsolidate = consolidationEnabled
    && (beforeContextItems >= consolidationMinItems || beforeContextChars >= consolidationMinChars);

  if (!(shouldConsolidate && Array.isArray(contextRetrieval?.items) && contextRetrieval.items.length > 0)) {
    emitConsolidateStage({
      status: 'skipped',
      decisionCode: consolidationEnabled ? 'consolidation_gate_not_met' : 'consolidation_disabled',
      triggered: false
    });
    return contextRetrieval;
  }

  const inputs = [];
  let usedChars = 0;
  for (const item of contextRetrieval.items) {
    if (inputs.length >= consolidationMaxInputItems) break;
    const text = String(item?.text || '').trim();
    if (!text) continue;
    const remaining = consolidationMaxInputChars - usedChars;
    if (remaining <= 20) break;
    const clipped = text.length <= remaining
      ? text
      : `${text.slice(0, Math.max(0, remaining - 3)).trimEnd()}...`;
    if (!clipped) continue;
    inputs.push({
      source: item?.source || 'unknown',
      docName: item?.meta?.docName || item?.meta?.label || null,
      chunkHash: item?.meta?.chunkHash || null,
      score: Number(item?.score ?? item?.baseScore ?? 0) || 0,
      text: clipped
    });
    usedChars += clipped.length;
  }

  const renderedInputs = inputs.map((entry, index) => {
    const ref = [
      `source=${entry.source}`,
      entry.docName ? `doc=${entry.docName}` : '',
      entry.chunkHash ? `chunk=${entry.chunkHash}` : ''
    ].filter(Boolean).join(' ');
    return `[#${index + 1}] ${ref}\n${entry.text}`;
  }).join('\n\n');
  const attemptStart = Date.now();

  try {
    const consolidationResponse = await requestCompletion({
      model: consolidationModel || undefined,
      messages: [
        {
          role: 'system',
          content: `You are a context condenser. Keep only evidence directly relevant to the user ask. Preserve exact facts and names. Output markdown bullets only. Max ${consolidationOutputMaxChars} chars.`
        },
        {
          role: 'user',
          content: [
            `User ask: ${String(userText || '').trim()}`,
            'Context excerpts:',
            renderedInputs,
            'Return concise markdown bullets with only relevant evidence and no commentary.'
          ].join('\n\n')
        }
      ],
      temperature: 0,
      max_tokens: Math.max(180, Math.min(1400, Math.floor(consolidationOutputMaxChars / 3))),
      stream: false,
      signal
    });

    const rawConsolidated = typeof normalizeContextText === 'function'
      ? normalizeContextText(extractResponseText(consolidationResponse))
      : String(extractResponseText(consolidationResponse) || '').trim();
    if (typeof onModelAttempt === 'function') {
      const usage = consolidationResponse?.usage && typeof consolidationResponse.usage === 'object'
        ? {
            promptTokens: Number(consolidationResponse.usage.prompt_tokens ?? consolidationResponse.usage.promptTokens ?? 0) || 0,
            completionTokens: Number(consolidationResponse.usage.completion_tokens ?? consolidationResponse.usage.completionTokens ?? 0) || 0,
            totalTokens: Number(consolidationResponse.usage.total_tokens ?? consolidationResponse.usage.totalTokens ?? 0) || 0
          }
        : { promptTokens: 0, completionTokens: 0, totalTokens: 0 };
      onModelAttempt({
        stageName: 'consolidate',
        callKind: 'consolidate',
        modelId: consolidationModel || null,
        responseModel: String(consolidationResponse?.model || consolidationModel || '').trim() || consolidationModel || null,
        providerRequestId: String(consolidationResponse?.id || '').trim() || null,
        usage,
        status: 'success',
        durationMs: Date.now() - attemptStart,
        errorCode: null,
        errorMessage: null
      });
    }
    const consolidatedText = rawConsolidated.length <= consolidationOutputMaxChars
      ? rawConsolidated
      : `${rawConsolidated.slice(0, Math.max(0, consolidationOutputMaxChars - 3)).trimEnd()}...`;

    if (!consolidatedText) {
      emitConsolidateStage({
        status: 'skipped',
        decisionCode: 'consolidation_empty_output',
        triggered: true
      });
      return contextRetrieval;
    }

    const keptOriginal = contextRetrieval.items.slice(0, consolidationKeepTopItems);
    const consolidationSummaryCandidateChunkHashes = Array.from(new Set(
      inputs
        .filter((entry) => {
          const chunkHash = String(entry?.chunkHash || '').trim();
          if (!chunkHash) return false;
          const score = Number(entry?.score || 0) || 0;
          return score >= consolidationSummaryMinScore;
        })
        .map((entry) => String(entry?.chunkHash || '').trim())
        .filter(Boolean)
    )).slice(0, consolidationSummaryMaxPerTurn);
    const consolidatedItem = {
      source: 'consolidatedContext',
      text: consolidatedText,
      baseScore: 3.4,
      recencyBoost: 0,
      meta: {
        trigger: 'consolidation_model',
        model: consolidationModel || null,
        sourceItemCount: inputs.length,
        sourceChars: usedChars
      }
    };
    const nextItems = [consolidatedItem, ...keptOriginal];
    const nextSelectedChars = nextItems.reduce((total, item) => total + String(item?.text || '').length, 0);

    const nextRetrieval = {
      ...contextRetrieval,
      items: nextItems,
      meta: {
        ...(contextRetrieval?.meta || {}),
        selectedChars: nextSelectedChars,
        selectedCount: nextItems.length,
        selectedSourceBreakdown: typeof summarizeSourceBreakdown === 'function' ? summarizeSourceBreakdown(nextItems) : {},
        selectedDocBreakdown: typeof summarizeDocSelectionBreakdown === 'function'
          ? summarizeDocSelectionBreakdown(nextItems)
          : [],
        consolidationApplied: true,
        consolidationModel: consolidationModel || null,
        consolidationInputItems: inputs.length,
        consolidationInputChars: usedChars,
        consolidationOutputChars: consolidatedText.length,
        consolidationKeptOriginalItems: keptOriginal.length,
        consolidationSummaryCandidateChunkHashes,
        consolidationSummaryCandidateCount: consolidationSummaryCandidateChunkHashes.length,
        preConsolidationItems: beforeContextItems,
        preConsolidationChars: beforeContextChars
      }
    };

    emitConsolidateStage({
      status: 'applied',
      decisionCode: 'consolidation_applied',
      triggered: true,
      afterContextItems: nextItems.length,
      afterContextChars: nextSelectedChars,
      consolidationOutputChars: consolidatedText.length,
      keptOriginalItems: keptOriginal.length
    });

    return nextRetrieval;
  } catch (error) {
    if (typeof onModelAttempt === 'function') {
      const errorMessage = String(error?.message || 'consolidation failed');
      const timeoutLike = /timeout|timed out|cancelled before completion/i.test(errorMessage);
      onModelAttempt({
        stageName: 'consolidate',
        callKind: 'consolidate',
        modelId: consolidationModel || null,
        responseModel: null,
        providerRequestId: null,
        usage: { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
        status: timeoutLike ? 'timeout' : 'failed',
        durationMs: Date.now() - attemptStart,
        errorCode: timeoutLike ? 'REQUEST_TIMEOUT' : (String(error?.code || '').trim() || null),
        errorMessage
      });
    }

    emitConsolidateStage({
      status: 'failed',
      decisionCode: 'consolidation_error',
      triggered: true,
      reasonCodes: ['consolidation_failed'],
      error: String(error?.message || 'consolidation failed')
    });
    return contextRetrieval;
  }
};

// Finalizes assistant text output and returns finalize-stage telemetry payload.
export const executeFinalizeStage = ({
  responseText = '',
  intentKey = 'ask',
  formatPolicy = null
} = {}) => {
  const finalized = finalizeAssistantResponseText({
    text: responseText,
    intentKey,
    formatPolicy
  });

  return {
    text: finalized,
    finalizeResult: createStageResult({
      stage: 'finalize',
      status: 'ok',
      decisionCode: 'finalize_completed',
      payload: {
        responseLength: String(finalized || '').length
      }
    })
  };
};

// Creates the emitted finalize-stage payload including grounding, usage, and cost fields.
export const createFinalizeStagePayload = ({
  finalizeResult = null,
  text = '',
  runtimeEnabled = false,
  usedLegacyRequest = false,
  responseModel = null,
  groundingStatus = 'grounding-skipped',
  groundingRetryUsed = false,
  groundingReasonCodes = [],
  groundingUngroundedEntities = [],
  usage = null,
  cost = null
} = {}) => {
  return {
    status: finalizeResult?.status || 'ok',
    decisionCode: finalizeResult?.decisionCode || 'finalize_completed',
    textChars: String(text || '').length,
    usedLegacyFlow: !Boolean(runtimeEnabled),
    usedLegacyRequest: Boolean(usedLegacyRequest),
    responseModel: responseModel || null,
    groundingStatus: String(groundingStatus || 'grounding-skipped'),
    groundingRetryUsed: Boolean(groundingRetryUsed),
    groundingReasonCodes: Array.from(new Set(Array.isArray(groundingReasonCodes) ? groundingReasonCodes : [])),
    groundingUngroundedEntities: Array.isArray(groundingUngroundedEntities) ? groundingUngroundedEntities : [],
    usage: usage && typeof usage === 'object'
      ? usage
      : { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
    cost: cost && typeof cost === 'object'
      ? cost
      : { totalCostUsd: 0 }
  };
};

// Assembles the assistant trace object for diagnostics and stage-by-stage auditing.
export const createAssistantTrace = ({
  requestId = null,
  mode = null,
  runtimeEnabled = false,
  routingMode = 'hybrid',
  usage = null,
  cost = null,
  userText = '',
  clipAuditText,
  intent = null,
  goalState = null,
  summarizeGoalState,
  goalStatePersistence = null,
  requestLogAudit = null,
  queryPlan = null,
  retrievalIterationCount = 0,
  contextSufficiency = null,
  contextRetrieval = null,
  summarizeContextItems,
  buildKeywordCoverage,
  typedContextSummary = null,
  typedPayloadAudit = null,
  stageTrace = []
} = {}) => {
  const normalizedStages = Array.isArray(stageTrace) ? stageTrace : [];
  const intentStage = [...normalizedStages].reverse().find((event) => String(event?.stage || '').trim().toLowerCase() === 'intent') || null;
  const intentContract = intent?.contract && typeof intent.contract === 'object' ? intent.contract : null;
  const intentContractRollup = {
    source: String(
      intentStage?.intentContractSource
      || intentStage?.contractSource
      || intentContract?.source
      || 'heuristic'
    ).trim() || 'heuristic',
    decisionCode: String(
      intentStage?.intentContractDecisionCode
      || intentStage?.contractDecisionCode
      || 'intent_contract_heuristic_default'
    ).trim() || 'intent_contract_heuristic_default',
    modelAttempted: Boolean(intentStage?.intentContractModelAttempted ?? intentStage?.contractModelAttempted),
    primaryKeywordCount: Number(
      intentStage?.intentContractPrimaryKeywordCount
      ?? intentStage?.primaryKeywordCount
      ?? (Array.isArray(intentContract?.primaryKeywords) ? intentContract.primaryKeywords.length : 0)
    ) || 0,
    secondaryKeywordCount: Number(
      intentStage?.intentContractSecondaryKeywordCount
      ?? intentStage?.secondaryKeywordCount
      ?? (Array.isArray(intentContract?.secondaryKeywords) ? intentContract.secondaryKeywords.length : 0)
    ) || 0,
    queryRewriteCount: Number(
      intentStage?.intentContractQueryRewriteCount
      ?? intentStage?.queryRewriteCount
      ?? (Array.isArray(intentContract?.queryRewrites) ? intentContract.queryRewrites.length : 0)
    ) || 0,
    docHintCount: Number(
      intentStage?.intentContractDocHintCount
      ?? intentStage?.docHintCount
      ?? (Array.isArray(intentContract?.docHints) ? intentContract.docHints.length : 0)
    ) || 0,
    budgetHintTier: String(intentContract?.budgetHint?.tier || intentStage?.budgetHintTier || '').trim() || null
  };

  return {
    requestId,
    mode,
    runtime: {
      enabled: Boolean(runtimeEnabled),
      routingMode: routingMode || 'hybrid'
    },
    usage,
    cost,
    contextAudit: {
      userText: typeof clipAuditText === 'function' ? clipAuditText(userText || '', 500) : String(userText || ''),
      mode,
      intent: {
        key: intent?.key || 'ask',
        confidence: Number(intent?.confidence || 0),
        source: intent?.source || 'heuristic',
        contractRollup: intentContractRollup
      },
      goalState: typeof summarizeGoalState === 'function' ? summarizeGoalState(goalState) : null,
      goalStatePersistence,
      requestLog: requestLogAudit,
      queryPlan: {
        queries: Array.isArray(queryPlan?.queries) ? queryPlan.queries : [],
        queryExpansion: queryPlan?.queryExpansion || null,
        disambiguation: queryPlan?.disambiguation || null,
        sourcePriorities: Array.isArray(queryPlan?.sourcePriorities) ? queryPlan.sourcePriorities : []
      },
      retrieval: {
        retrievalIterations: Number(retrievalIterationCount || 0),
        sufficiency: {
          isSufficient: Boolean(contextSufficiency?.isSufficient),
          decisionCode: contextSufficiency?.decisionCode || 'insufficient',
          reasonCodes: Array.isArray(contextSufficiency?.reasonCodes) ? contextSufficiency.reasonCodes : [],
          score: Number(contextSufficiency?.score || 0),
          thresholds: contextSufficiency?.thresholds || null
        },
        selectedCount: Number(contextRetrieval?.meta?.selectedCount || 0),
        selectedChars: Number(contextRetrieval?.meta?.selectedChars || 0),
        candidateCount: Number(contextRetrieval?.meta?.totalCandidates || 0),
        retrievalReasons: Array.isArray(contextRetrieval?.meta?.retrievalReasons) ? contextRetrieval.meta.retrievalReasons : [],
        sourceBreakdown: contextRetrieval?.meta?.sourceBreakdown || {},
        selectedSourceBreakdown: contextRetrieval?.meta?.selectedSourceBreakdown || {},
        projectSearchTriggered: Boolean(contextRetrieval?.meta?.projectSearchTriggered),
        projectSearchPlannedQueries: Array.isArray(contextRetrieval?.meta?.projectSearchPlannedQueries)
          ? contextRetrieval.meta.projectSearchPlannedQueries
          : [],
        projectSearchAttemptedQueries: Array.isArray(contextRetrieval?.meta?.projectSearchAttemptedQueries)
          ? contextRetrieval.meta.projectSearchAttemptedQueries
          : [],
        projectSearchErrors: Array.isArray(contextRetrieval?.meta?.projectSearchErrors)
          ? contextRetrieval.meta.projectSearchErrors
          : [],
        selectedItems: typeof summarizeContextItems === 'function'
          ? summarizeContextItems(contextRetrieval?.items)
          : [],
        keywordCoverage: typeof buildKeywordCoverage === 'function'
          ? buildKeywordCoverage({ userText, items: contextRetrieval?.items })
          : null
      },
      typedContext: {
        requiredBlocks: typedContextSummary?.requiredBlocks || [],
        optionalBlocks: typedContextSummary?.optionalBlocks || [],
        optionalBlockCounts: typedContextSummary?.optionalBlockCounts || {},
        payload: typedPayloadAudit
      }
    },
    stages: normalizedStages
  };
};
